import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useSelector } from 'react-redux'
import { getSelectedCustomerId } from '../selectors/customers'
import prepareHeaders from '../utils/prepareHeaders'

const baseUrl = '/secure/policies/dlp/dlp-compliance-policy'

export const purviewDlpApi = createApi({
  reducerPath: 'purviewDlp',
  tagTypes: ['DlpPolicy', 'DlpPolicies'],
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getDlpPolicies: builder.query({
      query: (customerId) => ({
        url: baseUrl,
        params: { customerId },
      }),
      transformResponse: (response) => response?.filter((policy) => policy?.Mode !== 'PendingDeletion') ?? [],
      providesTags: (result, error, customerId) => [{ type: 'DlpPolicies', id: customerId }],
    }),
    getDlpPolicyById: builder.query({
      query: ({ customerId, policyId }) => ({
        url: `${baseUrl}/${policyId}`,
        params: { customerId },
      }),
      transformResponse: (response) => {
        const { rawDataFlat, ...rest } = response ?? {}
        return { ...rawDataFlat, ...rest }
      },
      providesTags: (result, error, { policyId }) => [{ type: 'DlpPolicy', id: policyId }],
    }),
    deleteDlpPolicy: builder.mutation({
      query: ({ customerId, policyId }) => ({
        url: `${baseUrl}/${policyId}`,
        method: 'DELETE',
        params: { customerId },
      }),
      invalidatesTags: (result, error, { policyId }) => [{ type: 'DlpPolicy', id: policyId }],
    }),
    createDlpPolicy: builder.mutation({
      query: ({ customerId, payload, hubTaskSource }) => ({
        url: baseUrl,
        method: 'POST',
        params: { customerId, source: hubTaskSource },
        body: payload,
      }),
    }),
    updateDlpPolicy: builder.mutation({
      query: ({ customerId, policyId, payload }) => ({
        url: `${baseUrl}/${policyId}`,
        method: 'PUT',
        params: { customerId },
        body: payload,
      }),
      invalidatesTags: (result, error, { policyId }) => [{ type: 'DlpPolicy', id: policyId }],
    }),
  }),
})

export const {
  useGetDlpPoliciesQuery,
  useLazyGetDlpPoliciesQuery,
  useGetDlpPolicyByIdQuery,
  useLazyGetDlpPolicyByIdQuery,
  useDeleteDlpPolicyMutation,
  useCreateDlpPolicyMutation,
  useUpdateDlpPolicyMutation,
} = purviewDlpApi

export const useGetDlpPolicies = (options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetDlpPoliciesQuery(customerId, options)
}

export const useGetDlpPolicyById = ({ policyId }, options = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetDlpPolicyByIdQuery({ customerId, policyId }, options)
}
